import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  createCampaign,
  updateCampaign,
  getCampaign,
  listLists,
  previewCampaign,
  testCampaign,
  sendCampaign,
  campaignRecipients,
} from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Send, Eye, Save, MailCheck, AlertTriangle, Users } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";

const placeholders = [
  { key: "{{first_name}}", label: "First name" },
  { key: "{{last_name}}", label: "Last name" },
  { key: "{{email}}", label: "Email" },
  { key: "{{company}}", label: "Company" },
];

export default function CampaignComposer() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [fromName, setFromName] = useState("");
  const [fromEmail, setFromEmail] = useState("");
  const [replyTo, setReplyTo] = useState("");
  const [listIds, setListIds] = useState([]);
  const [lists, setLists] = useState([]);
  const [preview, setPreview] = useState(null);
  const [testOpen, setTestOpen] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [campaignId, setCampaignId] = useState(id || null);
  const [status, setStatus] = useState("draft");
  const [inboxMode, setInboxMode] = useState(false);
  const [recipients, setRecipients] = useState(null);

  const refreshRecipients = useCallback(async (cid) => {
    if (!cid) return;
    try {
      const r = await campaignRecipients(cid);
      setRecipients(r);
    } catch (e) {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    listLists().then(setLists).catch(() => toast.error("Failed to load lists"));
    if (isEdit) {
      getCampaign(id).then((c) => {
        setName(c.name);
        setSubject(c.subject);
        setBody(c.body);
        setFromName(c.from_name || "");
        setFromEmail(c.from_email || "");
        setReplyTo(c.reply_to || "");
        setListIds(c.list_ids || []);
        setStatus(c.status);
        setInboxMode(!!c.inbox_mode);
      });
    }
  }, [id, isEdit]);

  const refreshPreview = useCallback(async (cid) => {
    if (!cid) return;
    try {
      const p = await previewCampaign(cid);
      setPreview(p);
    } catch (e) {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (campaignId) {
      const t = setTimeout(() => refreshPreview(campaignId), 400);
      return () => clearTimeout(t);
    }
  }, [subject, body, campaignId, refreshPreview]);

  useEffect(() => {
    if (campaignId) refreshRecipients(campaignId);
  }, [campaignId, refreshRecipients]);

  const handleSave = async () => {
    if (!name.trim() || !subject.trim() || !body.trim()) {
      toast.error("Name, subject and body are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name,
        subject,
        body,
        from_name: fromName || null,
        from_email: fromEmail || null,
        reply_to: replyTo || null,
        list_ids: listIds,
        inbox_mode: inboxMode,
      };
      let c;
      if (campaignId) {
        c = await updateCampaign(campaignId, payload);
      } else {
        c = await createCampaign(payload);
        setCampaignId(c.id);
        window.history.replaceState({}, "", `/campaigns/${c.id}/edit`);
      }
      toast.success("Campaign saved");
      refreshPreview(c.id);
      refreshRecipients(c.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    }
    setSaving(false);
  };

  const insertPlaceholder = (ph, target) => {
    if (target === "subject") setSubject((s) => s + " " + ph);
    else setBody((b) => b + " " + ph);
  };

  const handleSendTest = async () => {
    if (!campaignId) {
      toast.error("Save the campaign first");
      return;
    }
    if (!testEmail) {
      toast.error("Enter a recipient email");
      return;
    }
    try {
      await testCampaign({ campaign_id: campaignId, recipient: testEmail });
      toast.success(`Test email sent to ${testEmail}`);
      setTestOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test send failed");
    }
  };

  const handleSendCampaign = async () => {
    if (!campaignId) {
      toast.error("Save the campaign first");
      return;
    }
    if (listIds.length === 0) {
      toast.error("Select at least one audience list");
      return;
    }
    if (!window.confirm("Send this campaign to all selected recipients?")) return;
    try {
      const res = await sendCampaign(campaignId);
      toast.success(`Sending to ${res.total_recipients} recipients`);
      navigate(`/campaigns/${campaignId}/report`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    }
  };

  const toggleList = (lid) => {
    setListIds((cur) => (cur.includes(lid) ? cur.filter((x) => x !== lid) : [...cur, lid]));
  };

  const canSend =
    campaignId &&
    status === "draft" &&
    recipients &&
    recipients.total_recipients > 0 &&
    recipients.missing_lists.length === 0;

  return (
    <div className="p-8 max-w-[1400px]" data-testid="composer-page">
      <Link to="/campaigns" className="inline-flex items-center gap-1.5 text-sm text-[#475569] hover:text-[#002FA7] mb-4">
        <ArrowLeft size={14} /> Back to campaigns
      </Link>

      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="section-label mb-2">{isEdit ? "Edit campaign" : "New campaign"}</div>
          <h1 className="text-3xl font-semibold tracking-tight text-[#0F172A]" style={{ fontFamily: "Outfit" }}>
            Compose
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setTestOpen(true)}
            className="btn-secondary inline-flex items-center gap-2"
            disabled={!campaignId}
            data-testid="btn-send-test"
          >
            <MailCheck size={14} /> Send Test
          </button>
          <button
            onClick={handleSave}
            className="btn-secondary inline-flex items-center gap-2"
            disabled={saving}
            data-testid="btn-save-campaign"
          >
            <Save size={14} /> {saving ? "Saving…" : "Save Draft"}
          </button>
          <button
            onClick={handleSendCampaign}
            className="btn-primary inline-flex items-center gap-2"
            disabled={!canSend}
            data-testid="btn-send-campaign"
          >
            <Send size={14} /> Send Campaign
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Editor */}
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-6 space-y-4">
          <div className="section-label">Editor</div>

          <div>
            <label className="block text-sm font-medium text-[#0F172A] mb-1.5">Campaign name (internal)</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Q1 Product Launch"
              className="field-input"
              data-testid="input-campaign-name"
            />
          </div>

          <label
            className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors ${
              inboxMode
                ? "bg-[#ECFDF5] border-[#A7F3D0]"
                : "bg-[#F8FAFC] border-[#E2E8F0] hover:border-[#CBD5E1]"
            }`}
            data-testid="toggle-inbox-mode-label"
          >
            <input
              type="checkbox"
              checked={inboxMode}
              onChange={(e) => setInboxMode(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-[#002FA7]"
              data-testid="toggle-inbox-mode"
            />
            <div className="flex-1">
              <div className="text-sm font-semibold text-[#0F172A]">
                Inbox Mode <span className="text-[10px] font-medium text-[#059669] bg-[#ECFDF5] border border-[#A7F3D0] rounded px-1.5 py-0.5 ml-1">RECOMMENDED</span>
              </div>
              <p className="text-xs text-[#475569] mt-1 leading-relaxed">
                Sends as <strong>plain text</strong>, disables pixel &amp; link tracking, and adds RFC 8058 one-click unsubscribe headers. This maximizes chances of landing in the <strong>Primary inbox</strong> (not Promotions/Spam). Trade-off: no open/click analytics for this campaign.
              </p>
            </div>
          </label>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-sm font-medium text-[#0F172A]">Subject</label>
              <div className="flex flex-wrap items-center gap-1">
                {placeholders.map((p) => (
                  <button
                    key={p.key}
                    onClick={() => insertPlaceholder(p.key, "subject")}
                    className="text-[10px] font-mono px-1.5 py-0.5 bg-[#F1F5F9] text-[#475569] rounded hover:bg-[#E5E9F6] hover:text-[#002FA7]"
                    data-testid={`btn-ph-subject-${p.key.replace(/[{}]/g, "")}`}
                  >
                    {p.key}
                  </button>
                ))}
              </div>
            </div>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Hey {{first_name}}, we've got news for {{company}}"
              className="field-input"
              data-testid="input-subject"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-sm font-medium text-[#0F172A]">Body</label>
              <div className="flex flex-wrap items-center gap-1">
                {placeholders.map((p) => (
                  <button
                    key={p.key}
                    onClick={() => insertPlaceholder(p.key, "body")}
                    className="text-[10px] font-mono px-1.5 py-0.5 bg-[#F1F5F9] text-[#475569] rounded hover:bg-[#E5E9F6] hover:text-[#002FA7]"
                    data-testid={`btn-ph-body-${p.key.replace(/[{}]/g, "")}`}
                  >
                    {p.key}
                  </button>
                ))}
              </div>
            </div>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder={"Hi {{first_name}},\n\nThanks for being part of {{company}}. Here's what's new..."}
              className="field-input font-mono text-[13px] leading-relaxed"
              rows={12}
              data-testid="textarea-body"
            />
            <div className="text-xs text-[#94A3B8] mt-2">
              Plain text is auto-converted to inbox-friendly HTML. You can also paste HTML directly.
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-1.5">From name</label>
              <input
                value={fromName}
                onChange={(e) => setFromName(e.target.value)}
                placeholder="(uses default from Settings)"
                className="field-input"
                data-testid="input-from-name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-1.5">From email</label>
              <input
                value={fromEmail}
                onChange={(e) => setFromEmail(e.target.value)}
                placeholder="hello@yourdomain.com"
                className="field-input"
                data-testid="input-from-email"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-[#0F172A] mb-1.5">Reply-to (optional)</label>
            <input
              value={replyTo}
              onChange={(e) => setReplyTo(e.target.value)}
              placeholder="support@yourdomain.com"
              className="field-input"
              data-testid="input-reply-to"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#0F172A] mb-2">Audience lists</label>
            {lists.length === 0 ? (
              <div className="text-sm text-[#94A3B8]">No lists yet. Create one from the Audience page.</div>
            ) : (
              <div className="space-y-2 border border-[#E2E8F0] rounded-md p-3 bg-[#F8FAFC]">
                {lists.map((l) => (
                  <label
                    key={l.id}
                    className="flex items-center gap-3 cursor-pointer text-sm text-[#0F172A]"
                    data-testid={`list-select-${l.id}`}
                  >
                    <Checkbox
                      checked={listIds.includes(l.id)}
                      onCheckedChange={() => toggleList(l.id)}
                      data-testid={`checkbox-list-${l.id}`}
                    />
                    <span className="flex-1">{l.name}</span>
                    <span className="text-xs text-[#94A3B8] tabular-nums">{l.contact_count} contacts</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {campaignId && recipients && (
            <div
              className={`rounded-md border p-4 ${
                recipients.total_recipients > 0 && recipients.missing_lists.length === 0
                  ? "bg-[#ECFDF5] border-[#A7F3D0]"
                  : "bg-[#FEF3C7] border-[#FCD34D]"
              }`}
              data-testid="recipients-summary"
            >
              <div className="flex items-start gap-3">
                {recipients.total_recipients > 0 && recipients.missing_lists.length === 0 ? (
                  <Users size={18} className="text-[#059669] mt-0.5 flex-shrink-0" strokeWidth={2} />
                ) : (
                  <AlertTriangle size={18} className="text-[#B45309] mt-0.5 flex-shrink-0" strokeWidth={2} />
                )}
                <div className="flex-1">
                  <div className="text-sm font-semibold text-[#0F172A]" data-testid="recipients-total">
                    {recipients.total_recipients} unique recipient
                    {recipients.total_recipients === 1 ? "" : "s"} will receive this campaign
                  </div>
                  {recipients.per_list.length > 0 && (
                    <ul className="text-xs text-[#475569] mt-2 space-y-0.5">
                      {recipients.per_list.map((p) => (
                        <li key={p.list_id} className={p.missing ? "text-[#B45309] font-medium" : ""}>
                          {p.missing ? "⚠ " : "• "}
                          {p.name}
                          {p.missing ? " — was deleted; unselect and pick a valid list" : ` — ${p.active} active contact${p.active === 1 ? "" : "s"}`}
                        </li>
                      ))}
                    </ul>
                  )}
                  {recipients.total_recipients === 0 && recipients.missing_lists.length === 0 && (
                    <div className="text-xs text-[#B45309] mt-1.5">
                      Your selected list(s) have no active contacts. Upload contacts first, then save.
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Preview */}
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-6" data-testid="preview-panel">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="section-label">Live preview</div>
              <div className="text-lg font-semibold text-[#0F172A]" style={{ fontFamily: "Outfit" }}>
                Inbox render
              </div>
            </div>
            <button
              className="btn-secondary inline-flex items-center gap-1.5 text-xs"
              onClick={() => refreshPreview(campaignId)}
              disabled={!campaignId}
              data-testid="btn-refresh-preview"
            >
              <Eye size={13} /> Refresh
            </button>
          </div>

          {!campaignId ? (
            <div className="border border-dashed border-[#CBD5E1] rounded-md p-8 text-center text-sm text-[#94A3B8]">
              Save your campaign as a draft to see the live preview.
            </div>
          ) : (
            <>
              <div className="border border-[#E2E8F0] rounded-md overflow-hidden">
                <div className="bg-[#F8FAFC] px-4 py-2.5 border-b border-[#E2E8F0]">
                  <div className="text-xs text-[#94A3B8]">Subject</div>
                  <div className="text-sm font-medium text-[#0F172A]" data-testid="preview-subject">
                    {preview?.subject || subject}
                  </div>
                </div>
                <iframe
                  title="preview"
                  srcDoc={preview?.html || ""}
                  className="w-full h-[520px] bg-white"
                  data-testid="preview-iframe"
                />
              </div>
              <div className="text-xs text-[#94A3B8] mt-3">
                Preview uses sample data: John Doe, Acme Inc.
              </div>
            </>
          )}
        </div>
      </div>

      {/* Test dialog */}
      <Dialog open={testOpen} onOpenChange={setTestOpen}>
        <DialogContent data-testid="dialog-test-email">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Outfit" }}>Send test email</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <label className="block text-sm font-medium text-[#0F172A]">Recipient</label>
            <input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="you@example.com"
              className="field-input"
              data-testid="input-test-email"
            />
            <p className="text-xs text-[#94A3B8]">
              Note: with Resend&apos;s default sender, test emails can only be delivered to your Resend account email. Verify a domain to send anywhere.
            </p>
          </div>
          <DialogFooter>
            <button className="btn-secondary" onClick={() => setTestOpen(false)}>
              Cancel
            </button>
            <button className="btn-primary" onClick={handleSendTest} data-testid="btn-confirm-test">
              Send Test
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
